import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { HealthscoreCockpitView, BRAND, IST_ZUSTAND_CATEGORIES, VERHALTEN_CATEGORIES, } from '@sitzkrieger/healthscore-cockpit';
import { generateUrl } from '../../../Utilities/api';
function formatDateTime(iso) {
    const date = new Date(iso);
    return `${date.toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: '2-digit' })} ${date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
}
function lowestCategory(categoryScores, candidates) {
    let lowestScore = 101;
    let lowestCategoryKey = null;
    for (const categoryKey of candidates) {
        const entry = categoryScores[categoryKey];
        if (entry !== undefined && entry.score < lowestScore) {
            lowestScore = entry.score;
            lowestCategoryKey = categoryKey;
        }
    }
    return lowestCategoryKey;
}
const HealthscoreCockpitV2 = ({ testUrl }) => {
    const [questionnaires, setQuestionnaires] = useState([]);
    const [reportSummaries, setReportSummaries] = useState([]);
    const [selectedKey, setSelectedKey] = useState(null);
    const [fullReport, setFullReport] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isReportLoading, setIsReportLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isOpeningBericht, setIsOpeningBericht] = useState(false);
    // Opens the member's healthscore "Bericht" (result page + PDF download) in a new
    // tab. The signed result link is minted server-side from the questionnaire token.
    const openBericht = async (token) => {
        setIsOpeningBericht(true);
        try {
            const response = await fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_result_link', { token }), { credentials: 'same-origin' });
            if (response.ok) {
                const data = await response.json();
                if (data.url !== undefined && data.url !== '') {
                    window.open(data.url, '_blank', 'noopener');
                }
            }
        }
        catch {
            // Best-effort — the cockpit stays usable if the link cannot be built.
        }
        finally {
            setIsOpeningBericht(false);
        }
    };
    // Load all data sources and auto-select the newest entry.
    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [questionnaireResponse, reportResponse] = await Promise.all([
                    fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_history'), { credentials: 'same-origin' }),
                    fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_reports'), { credentials: 'same-origin' }),
                ]);
                const questionnaireList = questionnaireResponse.ok ? await questionnaireResponse.json() : [];
                const reportList = reportResponse.ok ? await reportResponse.json() : [];
                setQuestionnaires(questionnaireList);
                setReportSummaries(reportList);
                // Prefer the newest report; fall back to newest questionnaire.
                // Reports win on a date tie because they carry richer data.
                const newestReport = reportList[0] ?? null;
                const newestQuestionnaire = questionnaireList[0] ?? null;
                if (newestReport !== null && (newestQuestionnaire === null || newestReport.generatedAt >= newestQuestionnaire.finishedAt)) {
                    setSelectedKey(`report:${newestReport.uuid}`);
                }
                else if (newestQuestionnaire !== null) {
                    setSelectedKey(`questionnaire:${newestQuestionnaire.token}`);
                }
            }
            catch {
                setHasError(true);
            }
            finally {
                setIsLoading(false);
            }
        };
        void fetchAll();
    }, []);
    // When a report is selected, lazy-load the full report. For a questionnaire selection nothing extra is fetched.
    useEffect(() => {
        if (selectedKey === null || !selectedKey.startsWith('report:')) {
            setFullReport(null);
            return;
        }
        const uuid = selectedKey.slice('report:'.length);
        setIsReportLoading(true);
        void fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_report', { uuid }), { credentials: 'same-origin' })
            .then(response => response.ok ? response.json() : null)
            .then(data => { setFullReport(data); setIsReportLoading(false); })
            .catch(() => { setFullReport(null); setIsReportLoading(false); });
    }, [selectedKey]);
    // Unified dropdown list — reports + questionnaires merged, newest first.
    const unifiedEntries = useMemo(() => {
        const reportEntries = reportSummaries.map(report => ({
            key: `report:${report.uuid}`,
            kind: 'report',
            referenceId: report.uuid,
            generatedAt: report.generatedAt,
            score: report.overallScore,
            label: `${formatDateTime(report.generatedAt)} — Score ${report.overallScore} (${report.dataSources.includes('trainer') ? 'Messung' : 'Fragebogen'})`,
        }));
        const questionnaireEntries = questionnaires
            // Questionnaires that are already represented by a report would otherwise appear twice.
            // Heuristic: hide questionnaires whose `finishedAt` date matches the date of any report.
            .filter(questionnaire => !reportSummaries.some(report => report.generatedAt.slice(0, 10) === questionnaire.finishedAt.slice(0, 10)))
            .map(questionnaire => ({
            key: `questionnaire:${questionnaire.token}`,
            kind: 'questionnaire',
            referenceId: questionnaire.token,
            generatedAt: questionnaire.finishedAt,
            score: questionnaire.score,
            label: `${formatDateTime(questionnaire.finishedAt)} — Score ${questionnaire.score} (Fragebogen)`,
        }));
        return [...reportEntries, ...questionnaireEntries].sort((a, b) => b.generatedAt.localeCompare(a.generatedAt));
    }, [reportSummaries, questionnaires]);
    // Resolve the active selection.
    const selectedEntry = selectedKey !== null ? unifiedEntries.find(entry => entry.key === selectedKey) ?? null : null;
    const isReportSelected = selectedEntry !== null && selectedEntry.kind === 'report';
    const isQuestionnaireSelected = selectedEntry !== null && selectedEntry.kind === 'questionnaire';
    const currentQuestionnaire = isQuestionnaireSelected && selectedEntry !== null
        ? questionnaires.find(questionnaire => questionnaire.token === selectedEntry.referenceId) ?? null
        : null;
    const categoryScores = isReportSelected && fullReport !== null
        ? fullReport.categoryScores
        : currentQuestionnaire !== null
            ? Object.fromEntries(Object.entries(currentQuestionnaire.groupScores).map(([key, value]) => [key, { score: Math.round(value), source: 'questionnaire' }]))
            : {};
    const overallScore = isReportSelected && fullReport !== null
        ? fullReport.overallScore
        : currentQuestionnaire !== null
            ? currentQuestionnaire.score
            : null;
    const fokusIstZustand = isReportSelected && fullReport !== null
        ? fullReport.fokusIstZustand
        : lowestCategory(categoryScores, IST_ZUSTAND_CATEGORIES);
    const fokusVerhalten = isReportSelected && fullReport !== null
        ? fullReport.fokusVerhalten
        : lowestCategory(categoryScores, VERHALTEN_CATEGORIES);
    const measurementTargets = isReportSelected && fullReport !== null ? fullReport.measurementTargets : [];
    // History for the detail panel: all reports + questionnaires mapped to HealthReportSummary.
    const historyReports = useMemo(() => {
        const fromReports = reportSummaries.map(report => ({
            uuid: report.uuid,
            overallScore: report.overallScore,
            leverCategory: null,
            focusCategories: [report.fokusIstZustand, report.fokusVerhalten].filter((category) => category !== null),
            dataSources: report.dataSources,
            generatedAt: report.generatedAt,
            generatedBy: '',
            categoryScores: report.categoryScores,
        }));
        const fromQuestionnaires = questionnaires.map(questionnaire => ({
            uuid: questionnaire.token,
            overallScore: questionnaire.score,
            leverCategory: null,
            focusCategories: [],
            dataSources: ['questionnaire'],
            generatedAt: questionnaire.finishedAt,
            generatedBy: '',
            categoryScores: Object.fromEntries(Object.entries(questionnaire.groupScores).map(([key, value]) => [key, Math.round(value)])),
        }));
        return [...fromReports, ...fromQuestionnaires].sort((a, b) => b.generatedAt.localeCompare(a.generatedAt));
    }, [reportSummaries, questionnaires]);
    const focusCategories = [fokusIstZustand, fokusVerhalten].filter((category) => category !== null);
    const showSkeleton = isLoading || isReportLoading;
    const fragebogenOnly = isQuestionnaireSelected || (fullReport !== null && !fullReport.dataSources.includes('trainer'));
    if (hasError)
        return <></>;
    return (<div className="flex flex-col gap-6">
            {/* Entry dropdown + CTA */}
            <div className="flex items-center gap-3 flex-wrap">
                {!isLoading && unifiedEntries.length > 0 && (<select value={selectedKey ?? ''} onChange={event => setSelectedKey(event.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2" style={{ minWidth: 280 }}>
                        {unifiedEntries.map(entry => (<option key={entry.key} value={entry.key}>{entry.label}</option>))}
                    </select>)}
                {!isLoading && unifiedEntries.length === 0 && (<span className="text-sm text-gray-400 italic">Noch kein Eintrag vorhanden</span>)}

                {!isLoading && selectedEntry !== null && selectedEntry.kind === 'questionnaire' && (<button type="button" onClick={() => void openBericht(selectedEntry.referenceId)} disabled={isOpeningBericht} className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors shrink-0 disabled:opacity-60" style={{ borderColor: BRAND.blue, color: BRAND.blue }}>
                        {isOpeningBericht ? 'Öffne…' : 'Zum Bericht'}
                    </button>)}

                <a href={testUrl} className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ml-auto shrink-0" style={{ backgroundColor: BRAND.blue }}>
                    Neuen Test starten
                </a>
            </div>

            <HealthscoreCockpitView categoryScores={showSkeleton ? {} : categoryScores} overallScore={showSkeleton ? null : overallScore} leverCategory={fokusIstZustand} focusCategories={focusCategories} measurementTargets={measurementTargets} reports={historyReports} fragebogenOnly={fragebogenOnly} audience="customer"/>
        </div>);
};
HealthscoreCockpitV2.displayName = 'HealthscoreCockpitV2';
export default HealthscoreCockpitV2;
